// ══════════════════════════════════════════════════════════════════════════════
// errorReporter.ts — Client-side error tracking con deduplicazione
// ══════════════════════════════════════════════════════════════════════════════

import BACKEND_URL from '../config.js';

const API_URL = BACKEND_URL;

// ── Token delle pagine pubbliche ──────────────────────────────────────────────
// I link pubblici di prenotazione contengono un token che dà accesso a dati di
// un acquirente (/prenota/gestione/:token permette perfino di annullare).
// Le segnalazioni finiscono in ErrorLog e nelle email: i token vanno mascherati
// prima, in un punto solo. Meglio mascherare troppo che troppo poco: il segmento
// va fino al primo separatore di percorso, query, spazio o virgolette.
const TOKEN = '[^/?#&\\s"\'<>)]+';
const RE_TOKEN_PAGINA = new RegExp(`(/prenota/(?:(?:a|gestione)/)?)${TOKEN}`, 'g');
const RE_TOKEN_API = new RegExp(`(/api/pubblico/prenotazioni/(?:(?:acquirente|gestione)/)?)${TOKEN}`, 'g');

/**
 * Sostituisce con `***` il token nei percorsi pubblici di prenotazione
 * (/prenota/…, /prenota/a/…, /prenota/gestione/… e /api/pubblico/prenotazioni/…).
 * Funziona su URL completi, percorsi e testo libero (messaggi, stack trace).
 * È idempotente e lascia invariato tutto ciò che non contiene un token.
 */
export function mascheraTokenPubblici(testo: string): string {
  return testo
    .replace(RE_TOKEN_PAGINA, '$1***')
    .replace(RE_TOKEN_API, '$1***');
}

const mascheraOpz = (testo?: string): string | undefined => (testo === undefined ? undefined : mascheraTokenPubblici(testo));

// ── Deduplicazione errori ─────────────────────────────────────────────────────
// Set<fingerprint> per evitare invio multiplo dello stesso errore
const sentErrors = new Set<string>();

/**
 * Calcola fingerprint di un errore
 */
function getFingerprint(tipo: string, message: string): string {
  return `${tipo}:${message}`;
}

/**
 * Verifica se l'errore è già stato inviato
 */
function isDuplicate(fingerprint: string): boolean {
  return sentErrors.has(fingerprint);
}

/**
 * Marca errore come inviato
 */
function markAsSent(fingerprint: string): void {
  sentErrors.add(fingerprint);
}

/**
 * Interfaccia dati errore
 */
interface ErrorData {
  tipo: 'frontend' | 'backend';
  message: string;
  stack?: string;
  url?: string;
  userId?: number | null;
  societaId?: number | null;
  userAgent?: string;
}

/**
 * Invia segnalazione errore al backend
 */
export async function reportError(datiGrezzi: ErrorData): Promise<void> {
  // Punto unico di uscita verso il backend: qui si mascherano i token delle pagine pubbliche
  // (url della pagina, URL delle fetch dentro il messaggio, stack) prima di loggare o inviare.
  const errorData: ErrorData = {
    ...datiGrezzi,
    message: mascheraTokenPubblici(datiGrezzi.message),
    stack: mascheraOpz(datiGrezzi.stack),
    url: mascheraOpz(datiGrezzi.url),
  };
  const fingerprint = getFingerprint(errorData.tipo, errorData.message);

  // Evita invio multiplo dello stesso errore
  if (isDuplicate(fingerprint)) {
    console.log(`[errorReporter] Errore già segnalato, skip: ${fingerprint}`);
    return;
  }

  try {
    // Recupera token JWT se disponibile (anche se la route è pubblica)
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };

    const response = await fetch(`${API_URL}/api/errors/report`, {
      method: 'POST',
      headers,
      body: JSON.stringify(errorData)
    });

    if (response.ok) {
      markAsSent(fingerprint);
      console.log(`[errorReporter] Errore segnalato con successo: ${fingerprint}`);
    } else {
      console.error(`[errorReporter] Errore nella segnalazione (status ${response.status})`);
    }
  } catch (err) {
    console.error('[errorReporter] Errore durante l\'invio della segnalazione:', err);
  }
}

/**
 * Recupera userId e societaId dallo storage locale (se disponibile)
 */
function getUserContext(): { userId: number | null; societaId: number | null } {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return { userId: null, societaId: null };

    const user = JSON.parse(userStr);
    return {
      userId: user.id || null,
      societaId: user.societaId || null
    };
  } catch {
    return { userId: null, societaId: null };
  }
}

// ── Global Error Handlers ─────────────────────────────────────────────────────

/**
 * Handler per errori JavaScript globali (window.onerror)
 */
export function setupWindowErrorHandler(): void {
  window.addEventListener('error', (event: ErrorEvent) => {
    const { userId, societaId } = getUserContext();

    reportError({
      tipo: 'frontend',
      message: event.message || 'Errore JavaScript sconosciuto',
      stack: event.error?.stack || undefined,
      url: window.location.href,
      userId,
      societaId,
      userAgent: navigator.userAgent
    });
  });
}

/**
 * Handler per promise rejection non gestite
 */
export function setupUnhandledRejectionHandler(): void {
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const { userId, societaId } = getUserContext();

    let message = 'Promise rejection non gestita';
    let stack: string | undefined;

    if (event.reason instanceof Error) {
      message = event.reason.message;
      stack = event.reason.stack;
    } else if (typeof event.reason === 'string') {
      message = event.reason;
    }

    reportError({
      tipo: 'frontend',
      message,
      stack,
      url: window.location.href,
      userId,
      societaId,
      userAgent: navigator.userAgent
    });
  });
}

/**
 * Intercetta errori fetch globali (opzionale, richiede monkey-patching)
 * Nota: questo è un approccio avanzato, usarlo con cautela
 */
export function setupFetchErrorInterceptor(): void {
  const originalFetch = window.fetch;

  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] instanceof Request ? args[0].url : String(args[0]));

    // Evita loop: non intercettare le chiamate al reporter stesso
    if (url.includes('/api/errors/report')) {
      return originalFetch(...args);
    }

    try {
      const response = await originalFetch(...args);

      // Se la risposta è un errore HTTP 5xx, segnala
      if (response.status >= 500) {
        const { userId, societaId } = getUserContext();

        reportError({
          tipo: 'frontend',
          message: `Errore HTTP ${response.status} su ${url}`,
          url: window.location.href,
          userId,
          societaId,
          userAgent: navigator.userAgent
        });
      }

      return response;
    } catch (err) {
      // Errore di rete (fetch fallito)
      const { userId, societaId } = getUserContext();

      reportError({
        tipo: 'frontend',
        message: `Errore di rete su ${url}: ${err instanceof Error ? err.message : 'Unknown'}`,
        stack: err instanceof Error ? err.stack : undefined,
        url: window.location.href,
        userId,
        societaId,
        userAgent: navigator.userAgent
      });

      throw err;
    }
  };
}

/**
 * Inizializza tutti gli error handlers
 */
export function initErrorReporter(): void {
  setupWindowErrorHandler();
  setupUnhandledRejectionHandler();
  setupFetchErrorInterceptor();
  console.log('[errorReporter] Error tracking initialized');
}
